import equal from 'fast-deep-equal';
import type { History, Location } from 'history';
import qs from 'qs';

import { asRecord, extractOwnFilters, URL_PREFIX } from './readSavedFilters';

const QS_OPTIONS = {
  addQueryPrefix: true,
  arrayFormat: 'brackets',
  encodeValuesOnly: true,
} as const;

// Значение фильтра → строковая форма для URL; Date нормализуется к UTC ISO (иначе String() даёт локализованный toString)
function serializeFilterValue(value: unknown): string {
  return value instanceof Date ? value.toISOString() : String(value);
}

// Значения фильтров → их строковая форма для URL
function serializeFilters(filters: Record<string, unknown>): Record<string, unknown> {
  const serialized: Record<string, unknown> = {};
  Object.entries(filters).forEach(([key, value]) => {
    serialized[key] = Array.isArray(value) ? value.map(serializeFilterValue) : serializeFilterValue(value);
  });
  return serialized;
}

/*
excludedKeys — корни PII-ключей: исключаются точные совпадения и семейства
«user_data.<поле>__<оператор>» (напр. user_data.full_name__eq), «email__like» и т.п.
*/
function isUrlExcludedKey(key: string, excludedKeys: string[]): boolean {
  return excludedKeys.some(
    (excluded) => key === excluded || key.startsWith(`${excluded}.`) || key.startsWith(`${excluded}__`),
  );
}

/*
Пишет в query string только отличия фильтров своей области («filters» / «filters[urlKey]») от дефолтов;
остальные параметры сохраняет. Массивы кодируются qs в формате brackets: filters[k][]=a&filters[k][]=b;
excludedKeys — корни ключей фильтров с персональными данными (ФИО, почта и т.п.), которые не должны попадать в адрес
*/
export function syncFiltersToUrl(
  location: Location,
  history: History,
  urlKey: string | undefined,
  appliedFilters: Record<string, unknown>,
  defaultFilters: Record<string, unknown>,
  excludedKeys: string[] = [],
): void {
  const changedFilters: Record<string, unknown> = {};
  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (isUrlExcludedKey(key, excludedKeys)) return;
    if (equal(value, defaultFilters[key])) return;
    changedFilters[key] = value;
  });

  const serializedOwnFilters = serializeFilters(changedFilters);

  const params = asRecord(qs.parse(location.search, { ignoreQueryPrefix: true })) ?? {};
  const filtersRoot = asRecord(params[URL_PREFIX]) ?? {};
  const ownFiltersFromUrl = extractOwnFilters(filtersRoot, urlKey);
  if (equal(ownFiltersFromUrl, serializedOwnFilters)) return;

  if (urlKey) {
    if (Object.keys(serializedOwnFilters).length > 0) filtersRoot[urlKey] = serializedOwnFilters;
    else delete filtersRoot[urlKey];
  } else {
    Object.keys(filtersRoot).forEach((key) => {
      if (!asRecord(filtersRoot[key])) delete filtersRoot[key];
    });
    Object.assign(filtersRoot, serializedOwnFilters);
  }

  if (Object.keys(filtersRoot).length > 0) params[URL_PREFIX] = filtersRoot;
  else delete params[URL_PREFIX];

  history.replace({ ...location, search: qs.stringify(params, QS_OPTIONS) });
}
